import { motion, useReducedMotion } from 'framer-motion';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import type { CustomProject } from '@/api/customProjects/types';

const cardSpring = { type: 'spring' as const, stiffness: 420, damping: 28, mass: 0.85 };

function formatAreaSqFt(areaSft: string): string {
  const t = areaSft.trim();
  if (!t) return '—';
  if (/sq\.?\s*ft/i.test(t)) return t;
  const n = Number(t.replace(/,/g, ''));
  if (!Number.isNaN(n) && Number.isFinite(n)) {
    return `${n.toLocaleString('en-IN')} sq.ft`;
  }
  return `${t} sq.ft`;
}

export type CustomProjectCardProps = {
  imageSrc?: string;
  project: CustomProject;
  onEnterWorkspace?: () => void;
};

export function CustomProjectCard({ imageSrc, project, onEnterWorkspace }: CustomProjectCardProps) {
  const reducedMotion = useReducedMotion();
  const { city, state } = project?.location || {};
  const locationLine = [city, state].filter(Boolean).join(', ') || '—';

  return (
    <Box
      component={motion.div}
      initial={false}
      transition={cardSpring}
      whileHover={reducedMotion ? undefined : { y: -5, scale: 1.02 }}
      whileTap={reducedMotion ? undefined : { scale: 0.985 }}
      sx={{
        width: 299,
        height: 239,
        borderRadius: '12px',
        bgcolor: '#fff',
        border: '1px solid #ececec',
        boxShadow:
          '2px 2px 4.2px rgba(43,43,43,0.11), 0px 0.808px 2.425px rgba(0,0,0,0.1), 0px 0.808px 1.617px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transformOrigin: 'center center',
        willChange: 'transform',
        transition: 'box-shadow 0.22s ease, border-color 0.22s ease',
        '&:hover': {
          boxShadow:
            '0px 12px 28px rgba(0, 0, 0, 0.1), 0px 4px 10px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(30, 42, 57, 0.06)',
          borderColor: 'rgba(30, 42, 57, 0.14)',
        },
      }}
    >
      <Box
        sx={{
          height: 113,
          position: 'relative',
          flexShrink: 0,
          background: 'linear-gradient(135deg, #e8eaef 0%, #d4d8e0 45%, #c5cad6 100%)',
        }}
      >
        {imageSrc ? (
          <Box
            component="img"
            src={imageSrc}
            alt={`${project.projectName} preview`}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : null}
      </Box>

      <Stack sx={{ px: 2, pt: 1.25, pb: 0, flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Tooltip
          title={project.projectName}
          disableHoverListener={project.projectName.length <= 30}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: '#1e2a39',
              lineHeight: 1.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {project.projectName}
          </Typography>
        </Tooltip>
        <Tooltip title={project.clientName} disableHoverListener={project.clientName.length <= 30}>
          <Typography
            noWrap
            sx={{
              fontSize: 11,
              color: '#4a5565',
              mt: 0.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {project.clientName}
          </Typography>
        </Tooltip>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ px: 2, py: 0.5, flexShrink: 0 }}
      >
        <PlaceOutlinedIcon sx={{ fontSize: 14, color: '#4a5565' }} />
        <Typography sx={{ fontSize: 11, color: '#4a5565', flex: 1, minWidth: 0 }} noWrap>
          {locationLine}
        </Typography>
        <Typography sx={{ fontSize: 11, color: '#4a5565', flexShrink: 0 }} noWrap>
          {formatAreaSqFt(project.metaData.areaSft)}
        </Typography>
      </Stack>

      <Box
        sx={{
          mt: 'auto',
          px: 1.5,
          py: 1,
          borderTop: '0.5px solid #ececec',
          bgcolor: 'rgba(236, 236, 236, 0.2)',
          borderRadius: '0 0 12px 12px',
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={onEnterWorkspace}
          sx={{
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            p: 0,
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.25,
            width: '100%',
            font: 'inherit',
          }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#1e2a39' }}>
            Enter Workspace
          </Typography>
          <ChevronRightIcon sx={{ fontSize: 14, color: '#1e2a39' }} />
        </Box>
      </Box>
    </Box>
  );
}
